export const en = {
  app: {
    title: 'Water Theft & Control Monitoring System',
    shortTitle: 'e-Tawan',
    department: 'Irrigation Department',
    government: 'Government of Punjab',
    tagline: 'Save Water, Secure Future',
  },

  theme: {
    command: 'Command',
    field: 'Field',
    switchLabel: 'Switch display theme',
  },

  language: {
    // Deliberately the NAME of the other language in its own script, so a user
    // who cannot read the current interface can still find the way out.
    switchTo: 'اردو',
  },

  nav: {
    primary: 'Primary navigation',
    dashboard: 'Dashboard',
    wires: 'Theft Wires',
    cases: 'Tawan Cases',
    recovery: 'Recovery',
    appeals: 'Appeals',
    publicReports: 'Public Reports',
    handovers: 'Handovers',
    audit: 'Audit Log',
    admin: 'Administration',
  },

  role: {
    secretary: 'Secretary Irrigation',
    chief_engineer: 'Chief Engineer',
    superintendent_engineer: 'Superintending Engineer',
    xen: 'Executive Engineer (XEN)',
    sdo: 'Sub-Divisional Officer (SDO)',
    sub_engineer: 'Sub-Engineer',
    zilladar: 'Zilladar',
    patwari: 'Canal Patwari',
    data_operator: 'Data Operator',
    admin: 'System Administrator',
    public_liaison: 'Public Liaison Cell',
  },

  auth: {
    signInTitle: 'Officer sign-in',
    signIn: 'Sign in',
    signInWithGoogle: 'Continue with Google',
    googleIntro:
      'Sign in with the Google account your administrator enrolled. There is no separate password for this system.',
    signingIn: 'Signing in…',
    signOut: 'Sign out',
    signOutAndRetry: 'Sign out and use a different account',
    checking: 'Checking your session…',
    notAuthorisedTitle: 'Signed in, but no access yet',
    notAuthorisedBody:
      'This Google account is not yet authorised for e-Tawan. If your administrator has just enrolled you, access usually arrives within fifteen minutes — leave this page open. If you have been waiting longer, or you signed in with a personal account by mistake, sign out and try the address your administrator enrolled.',
    accountsProvisionedNote:
      'Access is granted by the system administrator, to a specific Google address. There is no sign-up and no password to reset.',
    error: {
      accountDisabled:
        'Access for this account has been withdrawn. Contact your divisional administrator.',
      popupBlocked:
        'Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again.',
      providerDisabled:
        'Google sign-in is not switched on for this deployment. This is a configuration fault — report it to the system administrator.',
      tooManyAttempts: 'Too many attempts. Please wait a few minutes and try again.',
      network: 'Could not reach the server. Check your connection and try again.',
      unknown: 'Sign-in failed. Please try again.',
    },
    unprovisioned: {
      title: 'Account not yet assigned a role',
      body: 'Your sign-in worked, but no role or jurisdiction has been assigned to this account yet. Ask your system administrator to complete the setup. You will not be able to see any cases until they do.',
    },
  },

  setup: {
    title: 'Firebase is not configured',
    body: 'This build has no Firebase project connected, so sign-in is disabled. Copy .env.example and fill in the Firebase web configuration:',
  },

  // Honest stubs. Each names the phase that will build the screen and what the
  // officer will be able to do there — never invented data.
  phase: {
    dashboard:
      'The executive command centre is built in Phase 6, deliberately last: it is a view over data that does not exist until wires, cases and recovery are real. Building it first would produce an impressive screen full of invented numbers.',
    wires:
      'Phase 3 builds the digital wire: GPS capture, canal/RD/bank resolution, Mogha lookup, estimated discharge, and the officer attestation that replaces site photographs. Dispatch goes to the SDO with automatic copies to the XEN and Zilladar.',
    cases:
      'Phase 4 builds Parcha Tawan framing by the Patwari (or a Data Operator on their behalf, with both recorded), Zilladar verification, hearings, and the decision — with the penalty computed from the rate notification in force on the detection date.',
    recovery:
      'Phase 5 builds Challan 32-A entry, instalment vouchers, maker-checker verification, and treasury reconciliation — the compensating control for the removed voucher scan. Arrears are aged at 30/60/90+ days.',
    appeals:
      'Phase 5 builds the appeals ladder and the court stay tracker. A case under appeal or stay is reported separately and is excluded from administrative pendency.',
    publicReports:
      'Phase 2 builds the RDTB whistleblower portal and the officer inbox for routed citizen reports. Reports carry coordinates and a public video link only — no submitter identity of any kind.',
    audit:
      'Phase 7 builds the audit log viewer. Entries are append-only: no update and no delete, for anyone, including administrators.',
    admin:
      'Phase 7 builds user management with bulk CSV import (dry-run, row-level errors, rollback), routing rules, master data and penalty-schedule verification.',
  },

  placeholder: {
    yourCapabilities: 'What your role can do',
  },

  public: {
    nav: 'Public navigation',
    home: 'Home',
    report: 'Report theft',
    track: 'Check a report',
    officerSignIn: 'Officer sign-in',
  },

  platform: {
    youtube: 'YouTube',
    facebook: 'Facebook',
    x: 'X',
    instagram: 'Instagram',
    tiktok: 'TikTok',
  },

  landing: {
    heading: 'Report water theft on a Punjab canal',
    intro:
      'Water taken illegally upstream is water that never reaches the farmer at the tail. If you have seen a cut in a canal bank, an illegal pipe, or a pump lifting water without authorisation, you can report it here.',
    cta: 'Report water theft',

    privacy: {
      heading: 'Who will know that you reported',
      intro:
        'This form asks for your name, father’s name, CNIC, telephone number and address, because a report the Department cannot verify is a report it cannot act on. Those details are held by the Public Liaison Cell, which telephones you to confirm the report, and by the Secretary. They are not passed to the officers who go out to the canal.',
      weSeeHeading: 'What the Public Liaison Cell receives',
      weSee1: 'Your name, father’s name, CNIC, telephone number and address',
      weSee2: 'The link to the video you point us to',
      weSee3: 'The location, and anything you write in the notes',
      weDontHeading: 'What the field officers receive',
      weDont1: 'A ticket number and the location — never your name or CNIC',
      weDont2:
        'Not the video link either: footage of a canal bank can show who was standing there filming it',
      weDont3: 'A written description of the theft, prepared by the Cell, not by you',
      footnote:
        'We still do not record your IP address or anything about your device, and your tracking code is stored only as a one-way hash. Two roles in the whole system can see who filed a report, and the security rules refuse everybody else — including the officer who investigates it.',
    },

    impact: {
      heading: 'Why it matters',
      body: 'A canal is shared. Water diverted without authorisation at the head does not disappear — it is taken from the farmers at the tail, who often have the least ability to complain about it. Unauthorised use also damages banks and outlets, which then fail for everyone.',
    },

    law: {
      heading: 'The legal position',
      body: 'Unauthorised use of canal water is an offence under the Canal and Drainage Act. It can result in a special assessment charge (Tawan), removal of the illegal connection, and in serious cases a criminal case registered with the police. The penalty is assessed by the Sub-Divisional Officer after a hearing.',
    },

    how: {
      heading: 'How a report is handled',
      step1Title: 'You report',
      step1Body:
        'You give your details, the location, and a link to a video showing what you have seen. You receive a tracking code.',
      step2Title: 'The Public Liaison Cell telephones you',
      step2Body:
        'A member of the Cell calls to confirm who you are and what you saw, watches the video, and writes a description of it. From here on, that description is what travels — your details stay with the Cell.',
      step3Title: 'A zone takes it, and an officer investigates',
      step3Body:
        'The Cell raises a ticket to the Chief Engineer of the relevant zone, who assigns it to a sub-division. If theft is confirmed on site, a formal case is framed. Either way you can check the status with your code.',
    },

    safety: {
      heading: 'Please stay safe',
      body: 'Do not confront anyone and do not put yourself at risk to record a video. If a situation feels dangerous, do not approach it — report what you can safely see, or contact the police. Enforcement is the Department’s job, with the police and PERA where needed. It is not yours. Filing a report you know to be false is itself an offence, and one the Department may refer to the police.',
    },
  },

  report: {
    heading: 'Report water theft',

    /* The transition labels, which double as the buttons on the queue. */
    verifyIdentity: 'Reporter verified',
    dismiss: 'Close without forwarding',
    forward: 'Raise to the zone',
    forwardAgain: 'Raise again',
    accept: 'Accept into this zone',
    returnToCell: 'Return to the Cell',
    assignToSdo: 'Assign to a sub-division',
    reassign: 'Assign again',
    beginInvestigation: 'Begin investigation',
    wrongJurisdiction: 'Not this sub-division',
    convertToWire: 'Theft confirmed — open a case',
    closeNoTheft: 'Close — nothing found',
    markFake: 'Mark fabricated',

    intro:
      'Three things are needed: who you are, where it is happening, and a link to a video that shows it. Without the video link a report cannot be sent.',

    confidentialityTitle: 'Where your details go',
    confidentialityBody:
      'Your name, CNIC and telephone number are seen by the Public Liaison Cell, who will telephone you to confirm the report, and by the Secretary. They are not passed to the Chief Engineer or to the officer who visits the site — those officers receive a ticket number, the location, and a description written by the Cell. The video link stays with the Cell too, because footage of a canal bank can show who was standing there filming it.',

    reporterLegend: 'Who is reporting',
    reporterHelp:
      'The Cell telephones every reporter before a ticket goes any further. A report that cannot be verified cannot be acted on, which is why these are required rather than optional.',
    reporterName: 'Your name',
    reporterFatherName: 'Father’s name',
    reporterCnic: 'CNIC',
    reporterPhone: 'Mobile number',
    reporterAddress: 'Address',
    phoneHelp: 'Use a number you will answer. The Cell calls this number to confirm the report.',

    locationLegend: 'Where is it happening?',
    locationHelp:
      'Use your current location if you are at the spot, or type the coordinates. There is no map on this page on purpose — map tiles would tell another company where you are.',
    useMyLocation: 'Use my current location',
    locating: 'Finding your location…',
    gpsDenied:
      'Location access was refused. You can type the coordinates below instead, or allow location and try again.',
    gpsUnavailable: 'This device cannot provide a location. Please type the coordinates below.',
    latitude: 'Latitude',
    longitude: 'Longitude',
    locationHint: 'Canal and RD, if you know it (optional)',
    locationHintPlaceholder: 'BRB Canal, near RD 142+500, left bank',
    locationHintHelp:
      'Irrigation officers navigate by canal name, RD and bank. Even a rough description helps them find the spot.',

    videoLegend: 'Video evidence',
    videoHelp:
      'Paste a link to a video you have already published on YouTube, Facebook, X, Instagram or TikTok. Do not upload anything here — this system stores no files.',
    videoUrl: 'Link to the video',
    videoRecognised: 'Recognised as a {{platform}} link.',

    notesLegend: 'What did you see? (optional)',
    notesHelp: 'A few lines is enough. When it happens, and how, is the most useful thing.',
    notes: 'Notes',

    submit: 'Send report',
    submitting: 'Sending…',
    privacyReminder:
      'Your details go to the Public Liaison Cell and the Secretary. They are never passed to the officers who investigate. No IP address or device information is collected.',
    turnstileStubbed:
      'Bot protection is stubbed in this development build. It is always verified in production.',

    done: {
      heading: 'Report received',
      body: 'Your report has reached the Public Liaison Cell. Somebody will telephone you on the number you gave to confirm it before it goes any further.',
      codeLabel: 'Your tracking code',
      writeItDown:
        'Write this code down now. It is stored only as a one-way hash, so nobody — including the Department — can look it up or send it to you again.',
      checkStatus: 'Check a report',
      backHome: 'Back to home',
    },

    error: {
      coordinatesOutsidePunjab:
        'Those coordinates are outside Punjab. Please check the latitude and longitude.',
      nameRequired: 'Please give your full name.',
      fatherNameRequired: 'Please give your father’s name.',
      cnicInvalid: 'That CNIC does not look right. It is 13 digits, e.g. 35202-1234567-1.',
      phoneInvalid:
        'That mobile number does not look right. Use the form 03001234567 — the Cell will call it.',
      addressRequired: 'Please give an address the Cell can refer to.',
      videoRequired: 'A link to a video is required. A report cannot be sent without one.',
      videoNotRecognised:
        'That link was not recognised. It must be a public video on YouTube, Facebook, X, Instagram or TikTok.',
      notesTooLong: 'The notes are too long.',
      locationHintTooLong: 'The location description is too long.',
      turnstileMissing: 'Please wait for the bot check to finish, then send again.',
      turnstileFailed: 'The bot check did not pass. Please reload the page and try again.',
      notVerifiedYet: 'Telephone the reporter and record the call before forwarding this.',
      summaryTooShort:
        'Write what the video shows. This is the only description the Chief Engineer and the SDO will have — the video does not travel with the ticket.',
      zoneRequired: 'Choose the zone this belongs to.',
      subDivisionRequired: 'Choose the sub-division to assign this to.',
      fakeReasonTooShort:
        'Marking a report fabricated follows the reporter to their next call. Set out what you found, at length.',
      siteVisitRequired:
        'Give the reference for your site-visit recording. A fabrication mark needs the visit on record.',
      turnstileUnavailable:
        'The bot check could not load. Check your connection and reload the page.',
      busyTryLater:
        'The service is busy right now and could not accept your report. Please try again in a little while — nothing has been lost.',
      malformed: 'The report could not be read. Please reload the page and try again.',
      serverError: 'Something went wrong at our end. Please try again.',
      network: 'Could not reach the server. Check your connection and try again.',
      notConfigured: 'Reporting is not configured on this deployment yet.',
    },
  },

  track: {
    heading: 'Check a report',
    intro: 'Enter the tracking code you were given when you filed the report.',
    codeLabel: 'Tracking code',
    search: 'Check status',
    searching: 'Checking…',
    statusLabel: 'Current status',
    submittedOn: 'Filed on {{date}}',
    noOfficerDetails:
      'Only the progress is shown. Which officer is handling it is deliberately not disclosed here.',
    notFound:
      'No report matches that code. Check for typing mistakes — the code uses no letter O and no digit 0.',
    serverError: 'Could not check the status just now. Please try again shortly.',
  },

  reportState: {
    SUBMITTED: 'Received — awaiting the Cell',
    IDENTITY_VERIFIED: 'Reporter verified by telephone',
    FORWARDED: 'Raised to the zone',
    ACCEPTED: 'Accepted by the zone',
    WITH_SDO: 'With the Sub-Divisional Officer',
    UNDER_INVESTIGATION: 'Being investigated on site',
    CONVERTED_TO_WIRE: 'Theft confirmed — case opened',
    CLOSED_NO_THEFT: 'Closed — nothing found on inspection',
    MARKED_FAKE: 'Closed — found fabricated',
    RETURNED_TO_CELL: 'Returned to the Cell',
    RETURNED_TO_ZONE: 'Returned to the zone — wrong jurisdiction',
    DISMISSED_BY_CELL: 'Closed by the Cell',
  },

  inbox: {
    loading: 'Loading reports…',
    error: 'Could not load reports.',
    count: '{{count}} report(s)',
    empty: 'No citizen reports are waiting for you.',
    cellIntro:
      'Every report arrives here first. Telephone the reporter, watch the video, write what it shows, and raise a ticket to the zone. What you write is the only description the officers downstream will have.',
    fieldIntro:
      'These are tickets raised by the Public Liaison Cell. The reporter’s name and the video stay with the Cell — the description below is the Cell’s own account of what the video showed.',
    openTicket: 'Open',
    collapse: 'Close',
    nothingToDo: 'Nothing for you to do on this ticket at its current stage.',
    actions: 'What you can do',
    reason: 'Reason (recorded in the audit log)',

    corroborated: 'Reported {{count}} times',
    cellSummary: 'What the Cell recorded',
    noSummaryYet: 'The Cell has not written a description yet.',
    coordinates: 'Coordinates',
    locationHint: 'Location described',
    noHint: 'Not given',
    citizenNotes: 'What the citizen wrote',

    reporter: 'The reporter',
    reporterConfidential:
      'Visible to the Public Liaison Cell and the Secretary only. It is not on the ticket the zone or the sub-division receives.',
    reporterMissing:
      'This ticket has no reporter record. That should not happen — raise it with the administrator rather than acting on the ticket.',
    reporterName: 'Name',
    reporterFather: 'Father’s name',
    reporterCnic: 'CNIC',
    reporterPhone: 'Mobile',
    reporterAddress: 'Address',
    reporterHistory: 'Previous reports',
    historyLine: '{{total}} filed · {{confirmed}} confirmed · {{fake}} found fabricated',
    historyWarning:
      'Handle carefully: {{fake}} of this person’s {{total}} reports were found fabricated after a site visit.',
    warningSuggested:
      'Tell them on the call that a further fabricated report will be referred to the FIA or the local police, then record that you did.',
    alreadyWarned: 'This reporter has already been given that warning.',
    recordWarning: 'Record that the warning was given',

    callNote: 'What the reporter confirmed on the call',
    recordCall: 'Record the call',
    verified: 'Verified by telephone. {{note}}',

    summaryLabel: 'What the video shows',
    summaryPlaceholder:
      'e.g. A 4-inch pipe through the left bank about 200 ft below the bridge, running at night; filmed 12 September.',
    summaryHelp:
      'At least {{n}} characters. This is all the Chief Engineer and the SDO get — neither the video nor the citizen’s own words travel with the ticket.',
    zone: 'Zone',
    chooseZone: 'Choose a zone…',
    noZones:
      'No zones have been entered yet. An administrator must add them before a ticket can be raised to one.',
    noSubDivisions:
      'No sub-divisions have been entered for this zone yet. An administrator must add them before this ticket can be assigned.',
    orgUnreadable: 'The organisation chart could not be read: {{message}}',
    subDivision: 'Sub-division',
    chooseSubDivision: 'Choose a sub-division…',
    siteVisitReference: 'Site-visit recording reference',
    siteVisitHelp:
      'Where the recording of your visit can be found — your channel and the video title, or its link. A fabrication mark follows the reporter, so it needs the visit on record.',

    showVideo: 'Show video',
    hideVideo: 'Hide video',
    openOnPlatform: 'Open on {{platform}}',
    videoTitle: 'Citizen-submitted video evidence',
    videoMayBeDeleted:
      'This video is hosted by the platform and may be deleted by whoever posted it. Record what it shows in your description while you can.',
  },

  common: {
    back: 'Back',
    confirm: 'Confirm',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading…',
    signInRequired: 'Sign in to continue.',
  },

  unit: {
    cusecs: 'cusecs',
    acreFeet: 'acre-feet',
  },

  theftType: {
    cut_in_bank: 'Cut in the bank',
    pipe_installation: 'Illegal pipe installed',
    crest_tampering: 'Outlet crest tampered with',
    diesel_pump: 'Diesel pump lifting water',
    siphon: 'Siphon over the bank',
    gate_tampering: 'Gate tampered with',
    unauthorised_outlet: 'Unauthorised outlet',
    other: 'Other',
  },

  wireState: {
    DRAFT: 'Draft',
    DISPATCHED: 'With the SDO',
    SDO_CONFIRMED: 'Confirmed by the SDO',
    REJECTED: 'Rejected',
    CONVERTED_TO_TAWAN: 'Tawan case framed',
  },

  escalation: {
    none: 'Within the action window.',
    nextIn: 'Goes to {{level}} in about {{hours}} hour(s) if no action is taken.',
    NONE: '',
    AMBER_XEN: 'Reported to the Executive Engineer — the 48-hour window has passed.',
    AMBER_LOGGED: 'Still unactioned after 72 hours. Recorded against this sub-division.',
    RED_CE_SECRETARY: 'Reported to the Chief Engineer and the Secretary — over 7 days unactioned.',

    // Short forms, used INSIDE the "goes to X in N hours" sentence. The full
    // strings above are past-tense statements and read as nonsense when
    // interpolated into a forward-looking sentence.
    label: {
      NONE: '',
      AMBER_XEN: 'the Executive Engineer',
      AMBER_LOGGED: 'the divisional record',
      RED_CE_SECRETARY: 'the Chief Engineer and the Secretary',
    },
  },

  wire: {
    raiseNew: 'Raise a wire',
    newHeading: 'Raise a theft wire',
    loading: 'Loading wires…',
    loadError: 'Could not load wires.',
    empty: 'No wires yet.',
    justNow: 'Just now',

    locationLegend: 'Where is the theft?',
    locationHelp:
      'Capture your position if you are at the spot. Canal, RD and bank are what the SDO will use to find it — record them even when GPS is unavailable.',
    useMyLocation: 'Capture my position',
    locating: 'Capturing…',
    gpsDenied: 'Location access was refused. Canal, RD and bank below are enough to file the wire.',
    gpsUnavailable: 'This device cannot provide a location. Record the canal, RD and bank below.',
    canal: 'Canal',
    selectCanal: 'Select a canal…',
    rd: 'RD',
    rdHelp: 'Reduced Distance from the headworks, e.g. 142+500.',
    bank: 'Bank',
    bankLeft: 'Left bank (L/B)',
    bankRight: 'Right bank (R/B)',
    mogha: 'Mogha / outlet number (optional)',
    moghaPlaceholder: 'e.g. 142-R',
    moghaHelp: 'Record it when the theft is at an outlet rather than between outlets.',
    coordinates: 'Coordinates',
    noCoordinates: 'Not captured',

    theftLegend: 'What is happening?',
    theftType: 'Method',
    discharge: 'Estimated discharge',
    dischargeHelp: 'In cusecs. A single illegal pipe is usually well under 5. Maximum {{max}}.',

    attestationLegend: 'What did you see?',
    attestationHelp:
      'This system stores no photographs, so your written account IS the evidence. Describe what you found, where exactly, and what you did about it — this is what the hearing will read and what an appeal will review.',
    narrative: 'Your account',
    narrativeMore: '{{n}} more character(s) needed.',
    narrativeOk: 'Long enough to be acted on.',
    attestation: 'Officer account',

    dispatchToSdo: 'Dispatch to the SDO',
    saveDraft: 'Save as draft',
    offlineNote:
      'This works with no signal. The wire is stored on this device and sent automatically when a connection returns.',

    pendingSync: '{{count}} wire(s) are still on this device and have not reached the server yet.',
    offlineShowingCached: 'You are offline. Showing what is stored on this device.',
    notYetSynced: 'Not yet sent',

    confirm: 'Confirm theft',
    reject: 'Reject',
    rejectReason: 'Why is this being rejected? (recorded permanently)',
    confirmReject: 'Reject the wire',
    rejectedBecause: 'Rejected: {{reason}}',
    cannotJudgeOwn: 'You raised this wire, so another officer must confirm it.',

    error: {
      invalid: 'Please check the highlighted field.',
      canalRequired: 'Select the canal.',
      rdInvalid: 'Enter the RD as a number, or as RD+feet — for example 142+500.',
      bankRequired: 'Select which bank.',
      theftTypeRequired: 'Select how the water is being taken.',
      dischargeRequired: 'Enter the estimated discharge in cusecs.',
      dischargeTooSmall: 'That discharge looks too small. Enter the figure in cusecs.',
      dischargeTooLarge:
        'That is larger than a single theft normally is — check for a misplaced decimal point.',
      narrativeTooShort:
        'Describe what you saw in enough detail to be acted on at a hearing. With no photographs stored, your account is the evidence.',
      saveFailed: 'The wire could not be saved. Please try again.',
    },
  },

  season: {
    kharif: 'Kharif',
    rabi: 'Rabi',
  },

  crop: {
    wheat: 'Wheat',
    cotton: 'Cotton',
    sugarcane: 'Sugarcane',
    rice: 'Rice',
    maize: 'Maize',
    fodder: 'Fodder',
    orchard: 'Orchard',
    vegetables: 'Vegetables',
    other: 'Other',
  },

  caseState: {
    FRAMED: 'Framed',
    UNDER_VERIFICATION: 'With the Zilladar',
    VERIFIED: 'Countersigned — with the SDO',
    HEARING_SCHEDULED: 'Hearing scheduled',
    DECIDED: 'Decided',
    RECOVERY_PENDING: 'Recovery pending',
    PARTIALLY_RECOVERED: 'Partially recovered',
    FULLY_RECOVERED: 'Fully recovered',
    ARREARS_REFERRED: 'Referred as arrears',
    UNDER_APPEAL: 'Under appeal',
    STAYED: 'Stayed by court',
    VOID: 'Void',
  },

  outcome: {
    FINE_IMPOSED: 'Fine imposed',
    FIR_REGISTERED: 'FIR registered',
    CONNECTION_DISCONNECTED: 'Connection disconnected',
    METER_SEALED: 'Outlet sealed',
    WATER_RELEASED: 'Water released after penalty',
    WARNING: 'Warning',
    EXONERATED: 'Exonerated',
  },

  case: {
    submitForVerification: 'Send to the Zilladar',
    returnForCorrection: 'Return for correction',
    countersign: 'Countersign and put up to the SDO',
    scheduleHearing: 'Schedule a hearing',
    recordDecision: 'Record the decision',
    adjourn: 'Adjourn',
    raiseDemand: 'Raise the demand',
    recordPartialRecovery: 'Record partial recovery',
    recordFullRecovery: 'Record full recovery',
    referToTehsildar: 'Refer as arrears',
    recordArrearsRecovery: 'Record arrears recovery',
    recordAppealFiled: 'Record an appeal',
    recordAppealDecision: 'Record the appeal decision',
    remandForRehearing: 'Remand for rehearing',
    recordStayOrder: 'Record a stay order',
    vacateStay: 'Vacate the stay',
    void: 'Void this case',
  },

  tawan: {
    season: 'Season charged',
    seasonChoose: 'Choose the season…',
    multiplierLabel: 'Penalty, as a multiple of the Abiana',
    multiplierHelp:
      'Your judgement at the hearing: one to ten times, weighing how grave the theft was and what the accused said. The amount below follows from it — it is not typed in.',
    penaltyDecidedAtHearing:
      'Plain Abiana at ×1, for reference. The season charged and the multiplier — one to ten times — are decided by the SDO at the hearing, so the actual demand is not known yet.',
    baseKharif: 'If charged as Kharif',
    baseRabi: 'If charged as Rabi',
    count: '{{count}} case(s)',
    loading: 'Loading cases…',
    loadError: 'Could not load cases.',
    empty: 'No cases yet.',
    awaitingNumber: 'Number pending',
    unnumberedWire: 'Wire (number pending)',
    notYetSynced: 'Not yet sent',
    pendingSync: '{{count}} case(s) are still on this device and have not reached the server yet.',
    offlineShowingCached: 'You are offline. Showing what is stored on this device.',
    chakShort: 'Chak {{chak}}',

    framableHeading: 'Confirmed wires awaiting a Parcha Tawan',
    noFramableWires:
      'No confirmed wires are waiting. A case is always framed from a confirmed wire.',
    frameFromWire: 'Frame a case',

    frameHeading: 'Frame a Parcha Tawan',
    frameCase: 'Frame the case',

    onBehalfLegend: 'On whose behalf?',
    onBehalfHelp:
      'You are entering this as a Data Operator. The Patwari remains answerable for the case; you are answerable for the entry. Both are recorded.',
    patwariUid: 'Patwari account id',
    patwariUidHelp: 'The Patwari who framed this Parcha Tawan.',

    accusedLegend: 'The accused',
    accusedHelp:
      'Recorded, not verified. NADRA exposes no public verification, so the CNIC is checked for format only — nothing here confirms identity.',
    name: 'Name',
    nameUr: 'Name (Urdu)',
    fatherName: "Father's name",
    fatherNameUr: "Father's name (Urdu)",
    urduHelp: 'Printed on the Parcha Tawan and the hearing notice.',
    cnic: 'CNIC',
    cnicHelp: 'Format checked only. This does not verify the person.',
    phone: 'Phone (optional)',

    landLegend: 'The land',
    landHelp: 'As recorded in the revenue record.',
    chakNo: 'Chak number',
    mauza: 'Mauza',
    khasraNumbers: 'Khasra numbers',
    khasraHelp: 'Separate with commas or spaces. {{count}} recognised so far.',

    cropLegend: 'Crop and area irrigated without authorisation',
    cropHelp:
      'Enter the area in acres, kanals and marlas as measured. 8 kanal to the acre, 20 marla to the kanal.',
    crop: 'Crop',
    acres: 'Acres',
    kanals: 'Kanals',
    marlas: 'Marlas',
    totalAcres: 'Total: {{acres}} acres',
    areaAcres: 'Area (acres)',

    penaltyIndicative: 'Indicative penalty',
    penaltyUnavailable: 'No penalty figure can be produced',
    penaltyStillFrame:
      'You can still frame the case. Framing records the facts; the fine is set by the adjudicating officer once a verified rate notification is loaded.',
    penaltyNotPriced:
      'This case has no penalty figure — no verified rate notification was in force.',
    penaltyNoSchedules: 'No rate notification has been loaded into the system yet.',
    penaltyAwaitingArea: 'Enter the area to see an indicative penalty.',
    penaltySchedule:
      'Priced under {{version}} (notification {{notification}}), at the rate in force when the theft was found.',
    ratePerAcre: 'Rate per acre',
    multiplier: 'Multiplier',
    computedAmount: 'Computed',

    stanceLegend: "The accused's statement (optional)",
    stanceHelp: 'What the accused said when the case was put to them, if anything.',

    framedByLine: 'Framed by {{patwari}} · entered by {{operator}}',
    availableActions: 'What you can do next',
    reasonRequired: 'Reason (recorded permanently in the audit log)',
    cannotDecideOwn: 'This case arises from a wire you raised, so another officer must decide it.',

    decisionLegend: 'Record the decision',
    outcome: 'Outcome',
    fineAmount: 'Fine (Rs.)',
    decisionRemarks: 'Reasons for the decision',
    remarksHelp:
      'This is what an appellate authority will read. "Fine imposed" on its own is not something anyone can review.',
    recordDecision: 'Record the decision',

    recordFir: 'Record an FIR',
    firLegend: 'FIR details',
    firHelp:
      'Recorded here as a reference only. No copy of the FIR is stored — note where the paper is filed in the case notes.',
    firRegistered: 'FIR registered',
    firNumber: 'FIR number',
    policeStation: 'Police station',
    firDate: 'Date of FIR',
    firSections: 'Sections',
    accusedCount: 'Persons nominated',
    arrestedCount: 'Persons arrested',
    peraInvolved: 'PERA involved',

    error: {
      seasonRequired: 'Choose which season is being charged. The rate depends on it.',
      invalid: 'Please check the highlighted field.',
      wireRequired: 'A case must arise from a confirmed wire.',
      patwariRequired: 'Record which Patwari framed this case.',
      nameRequired: "Enter the accused's name.",
      fatherNameRequired: "Enter the father's name.",
      cnicInvalid: 'Enter a valid 13-digit CNIC.',
      phoneInvalid: 'That does not look like a Pakistani mobile number.',
      chakRequired: 'Enter the Chak number.',
      mauzaRequired: 'Enter the Mauza.',
      khasraRequired: 'Enter at least one Khasra number.',
      khasraInvalid: 'One of the Khasra numbers is not in a recognised form.',
      cropRequired: 'Select the crop.',
      areaRequired: 'Enter the area irrigated without authorisation.',
      areaTooLarge:
        'That area is larger than a single case normally covers — check acres and kanals are in the right boxes.',
      outcomeRequired: 'Select an outcome.',
      fineRequired: 'Enter the fine amount.',
      fineOnNonFineOutcome:
        'This outcome does not impose a fine, so no amount can be recorded against it.',
      remarksTooShort: 'Write reasons an appellate authority could review.',
      firNumberRequired: 'Enter the FIR number.',
      policeStationRequired: 'Enter the police station.',
      firDateRequired: 'Enter the date of the FIR.',
      firSectionsRequired: 'Enter the sections applied.',
      arrestedExceedsAccused: 'More people arrested than were nominated — check both figures.',
      saveFailed: 'The case could not be saved. Please try again.',
    },
  },

  transition: {
    notAllowedFromState: 'That action is not available from the current status.',
    roleNotPermitted: 'Your role cannot perform that action.',
    reasonRequired: 'A written reason is required for this action.',
    selfActionForbidden: 'You cannot take this action on a record you originated.',
  },

  rbac: {
    capabilityNotHeld: 'Your role does not include this permission.',
    outsideJurisdiction: 'That record is outside your jurisdiction.',
    selfActionForbidden: 'You cannot act on a record you originated.',
    makerCheckerViolation:
      'Verification must be done by someone other than the person who entered it.',
  },

  penalty: {
    noScheduleInForce: 'No rate notification was in force on the date this theft was detected.',
    scheduleNotVerified:
      'The rate notification has not been verified by an administrator, so it cannot be used to price a real case.',
    noRateForCrop: 'The rate notification has no rate for this crop.',
    areaRequired: 'Area must be greater than zero.',
    noBandForOffenceCount: 'No penalty band applies to this number of prior offences.',
  },

  observation: {
    kindRequired: 'Select what kind of observation this is.',
    narrativeTooShort:
      'Describe what you observed in enough detail to be acted on at a hearing. Since no photographs are stored, your account is the evidence.',
    attributionRequired: 'An observation must record who made it.',
  },

  documentRef: {
    typeRequired: 'Select the type of document.',
    referenceNumberRequired: 'Enter the document or reference number.',
    issuedByRequired: 'Enter the issuing office, police station, bank branch or court.',
    issuedDateRequired: 'Enter the date the document was issued.',
  },

  reconciliation: {
    CLAIMED: 'Claimed — not yet verified',
    OFFICER_VERIFIED: 'Verified by a second officer',
    TREASURY_RECONCILED: 'Confirmed by the treasury',
    RECONCILIATION_FAILED: 'Treasury has no such credit',
  },

  voucher: {
    action: {
      OFFICER_VERIFIED: 'Verify against the receipt',
      TREASURY_RECONCILED: 'Confirm against the treasury scroll',
      RECONCILIATION_FAILED: 'Record reconciliation failure',
    },
    error: {
      challanNumberInvalid: 'Enter a valid Challan 32-A number.',
      challanNumberDuplicate:
        'This challan number is already recorded against this case. One receipt cannot be banked twice.',
      bankRequired: 'Enter the bank.',
      branchCodeRequired: 'Enter the branch code.',
      depositDateRequired: 'Enter the deposit date.',
      depositDateInFuture: 'A deposit dated in the future has not happened yet.',
      amountRequired: 'Enter the amount deposited.',
      amountNotWholeRupees: 'Enter whole rupees. A fractional amount will never reconcile.',
      exceedsDemand:
        'This would recover more than the demand. The excess is public money collected without an order behind it — reconcile the figures first.',
      transitionNotAllowed: 'That step is not available from this state.',
      roleNotPermitted: 'Your role may not take this step.',
      rankTooJunior: 'Treasury reconciliation is recorded by an XEN or above.',
      reasonRequired: 'A written reason is required.',
      sameAsEnteringOfficer:
        'The officer who entered a voucher cannot verify it. A second person must.',
      sameAsVerifyingOfficer:
        'The officer who verified a voucher cannot also reconcile it. Reconciliation is a third pair of eyes.',
    },
  },

  recovery: {
    count: '{{count}} case(s) carrying a demand',
    noneYet: 'No case has reached recovery yet.',
    vouchers: 'Challans on this case',
    noVouchers: 'Nothing has been banked against this case yet.',
    enterChallan: 'Record a Challan 32-A',
    enterChallanNote:
      'No receipt image is held anywhere in this system. The challan number, bank, branch and deposit date are the whole of the assertion, and a second officer must verify them.',
    normalisedAs: 'Will be filed under',
    recordClaim: 'Record as claimed',
    entryBlocked: 'No challan can be recorded against this case at the moment.',
    enteredBy: 'Entered by',
    verifiedBy: 'Verified by',
    reconciledBy: 'Reconciled by',
    treasuryScroll: 'Treasury scroll',
    notYet: 'not yet',
    failureReason: 'Reason',
    reasonLabel: 'Written reason',
    treasuryScrollLabel: 'Treasury scroll / credit reference',
    blocked: {
      stay: 'Recovery is stopped: a court stay is in force on this case.',
      unknown: 'Recovery cannot proceed on this case at the moment.',
    },
    figure: {
      demand: 'Demand',
      originalDemand: 'As originally imposed',
      revisedOnAppeal: 'Revised on appeal at level {{level}}',
      recovered: 'Recovered',
      outstanding: 'Outstanding',
      proven: 'Confirmed by the treasury',
      provenNote: 'Money proven to have reached government account.',
      unproven: 'Not yet confirmed',
      unprovenNote:
        'Two officers say the receipt exists; the treasury has not yet confirmed the credit.',
      failed: 'Treasury denied',
      failedNote: 'The treasury has no record of this deposit. Never counted as recovery.',
      stayedDays: 'Days under stay',
      stayedDaysNote:
        'Subtracted from arrears ageing — the department could not act on these days.',
    },
    field: {
      challan: 'Challan 32-A number',
      bank: 'Bank',
      branch: 'Branch code',
      amount: 'Amount deposited (Rs)',
      depositDate: 'Deposit date',
      fileLocation: 'Where the paper receipt is filed',
      court: 'Court',
      courtCaseNumber: 'Court case number',
      grantedAt: 'Stay granted on',
    },
    stays: 'Court stays',
    noStays: 'No stay has been recorded on this case.',
    recordStay: 'Record a stay order',
    recordStayBlocked:
      'A stay is already in force. Record its vacation before entering another, so the history stays readable.',
    stayRunning: 'In force',
    stayVacated: 'Vacated',
    stayedDaysSoFar: '{{count}} day(s) so far',
    vacatedReason: 'Ground for vacation',
    vacateReasonLabel: 'Ground for vacating (recovery restarts)',
    vacateStay: 'Record vacation',
  },

  appealOutcome: {
    UPHELD: 'Upheld',
    MODIFIED: 'Modified',
    SET_ASIDE: 'Set aside',
    REMANDED: 'Remanded for fresh hearing',
  },

  appeal: {
    noneYet: 'No decided case is available to appeal.',
    noAppealsOnCase: 'No appeal has been filed on this case.',
    orderUnderChallenge: 'The order under challenge',
    originalOrder: '{{outcome}} — Rs {{amount}}',
    noDecisionYet: 'This case has not been decided yet.',
    supersededBy: 'Superseded at appeal level {{level}}: Rs {{amount}} is now recoverable.',
    level: 'Appeal — level {{level}}',
    pending: 'Pending',
    fileAppeal: 'Record an appeal at level {{level}}',
    appellantName: 'Appellant',
    filedDate: 'Filed on',
    groundsLabel: 'Grounds of appeal',
    groundsHint:
      'An appellate authority cannot review "unfair" — and neither can the next authority up, reading this a year later.',
    suspensionRefusedLabel: 'The appellate authority REFUSED to suspend recovery',
    suspensionRefused: 'Recovery continues: suspension was refused by the appellate authority.',
    recordFiling: 'Record the filing',
    outcomeLabel: 'Outcome',
    revisedAmount: 'Revised fine (Rs)',
    orderNumber: 'Appellate order number',
    issuedBy: 'Issued by',
    issuedDate: 'Order date',
    fileLocation: 'Where the order is filed',
    recordDecision: 'Record the appellate order',
    recusedFromDeciding:
      'You may not decide this appeal: it challenges your own order, or you recorded its filing.',
    limitationUnknown:
      'Whether this appeal is in time cannot be determined: the Department has not yet notified the appellate ladder or the limitation period.',
    filedWithin: 'Filed {{days}} day(s) after the order.',
    limitation: {
      ladderNotVerified:
        'The appellate ladder has not been verified by the Department, so no appeal can be declared time-barred.',
      levelNotInLadder: 'This appeal level is not in the notified ladder.',
    },
    demand: {
      setAside: 'The order was set aside on appeal. There is no demand to recover.',
      remanded:
        'The case was remanded for fresh hearing. There is no operative order to recover under.',
      suspendedPendingAppeal: 'Recovery is suspended while an appeal is pending.',
    },
    error: {
      levelRequired: 'Enter the appeal level.',
      appellantRequired: "Enter the appellant's name.",
      filedDateRequired: 'Enter the date the appeal was filed.',
      appealedOrderDateRequired: 'The date of the order under challenge is missing.',
      filedBeforeOrder: 'An appeal cannot be dated before the order it challenges.',
      groundsTooShort: 'State the grounds in enough detail for an appellate authority to review.',
      outcomeRequired: 'Select the outcome.',
      decidedDateRequired: 'Enter the date of the appellate order.',
      revisedAmountRequired: 'A modified outcome needs the revised fine.',
      revisedAmountNotWholeRupees: 'Enter whole rupees.',
      revisedAmountOnNonModified:
        'Only a MODIFIED outcome may carry a revised figure. Anywhere else it is a demand with no order behind it.',
      orderReferenceRequired:
        'Identify the appellate order: its number, who issued it, and its date.',
    },
  },

  stay: {
    error: {
      courtRequired: 'Enter the court.',
      caseNumberRequired: 'Enter the court case number.',
      grantedAtRequired: 'Enter the date the stay was granted.',
      alreadyStayed:
        'A stay is already in force on this case. Record the earlier one as vacated first, so the history stays readable.',
      alreadyVacated: 'This stay has already been vacated. A fresh stay is a new episode.',
      vacatedBeforeGranted: 'A stay cannot be vacated before it was granted.',
      reasonRequired: 'Vacating restarts recovery against a person. State the ground.',
    },
  },

  kpi: {
    totalCases: 'Total cases',
    pendingCases: 'Pending',
    pendingNote: 'Waiting on the department. Excludes cases under a court stay.',
    stayedCases: 'Under court stay',
    stayedNote: 'Reported separately, never as administrative delay.',
    decidedCases: 'Decided',
    firRegistered: 'FIR registered',
    finesImposed: 'Fines imposed',
    finesRecovered: 'Recovered',
    arrears: 'Arrears',
    arrearsNote: 'Excludes cases under a court stay.',
    unprovenRecovery: 'Not yet confirmed',
    unprovenNote: 'Two officers signed; the treasury has not confirmed the credit.',
    failedReconciliation: 'Treasury denied',
    failedNote: 'No treasury record of these deposits. Escalated.',
  },

  ageing: {
    current: 'Under 30 days',
    days_30: '30–59 days',
    days_60: '60–89 days',
    days_90_plus: '90 days and over',
  },

  zone: {
    bahawalpur: 'Bahawalpur',
    sargodha: 'Sargodha',
    sahiwal: 'Sahiwal',
    faisalabad: 'Faisalabad',
    multan: 'Multan',
    lahore: 'Lahore',
    dg_khan: 'D.G. Khan',
  },

  dashboard: {
    noRollupYet:
      'No figures have been computed for your jurisdiction yet. The nightly job builds them; this is not the same as having no cases.',
    staleWarning: 'These figures are more than a day old. The nightly recompute may not have run.',
    recomputedAt: 'Figures as at {{at}}',
    reads: 'built from {{count}} rollup document(s), not from the case records',
    caseTrend: 'Cases over six months',
    moneyTrend: 'Imposed against recovered (Rs)',
    outcomes: 'Decisions and outcomes',
    topCanals: 'Canals with the most cases',
    ageing: 'Arrears by age',
    theftTypes: 'How the water was taken',
    pendingByZone: 'Pending by zone',
    noZoneData: 'No zone figures are available.',
    recentActivity: 'Recent activity',
    activityRestricted:
      'The audit log is open to the Secretary and the system administrator only. That is deliberate: an append-only record of who did what is an oversight instrument, not a management convenience.',
    noActivity: 'Nothing has been recorded yet.',
    exportCsv: 'Export CSV',
    backToProvince: 'Back to the whole province',
    csv: {
      scope: 'Scope',
      asAt: 'Figures as at',
      figure: 'Figure',
      value: 'Value (Rs where applicable)',
      monthly: 'Month by month',
      month: 'Month',
    },
    zoneComparison: 'Zone by zone',
    zone: 'Zone',
    zoneNoData: 'not yet computed',
    noDecisionsYet: 'No case has been decided yet.',
    noCanalData: 'No canal has been recorded against a case yet.',
    noArrears: 'Nothing is outstanding.',
    noTheftData: 'No theft type has been recorded yet.',
    showTable: 'Show these figures as a table',
    hideTable: 'Hide the table',
    tableCaption: 'Every figure on this page, in text. Read from {{count}} rollup document(s).',
    series: {
      framed: 'Cases framed',
      decided: 'Decided',
      imposed: 'Imposed',
      recovered: 'Recovered',
    },
  },

  insights: {
    mine: 'Your decisions',
    mineIntro:
      'Every case you have decided in this jurisdiction, and what has been recovered against your orders since.',
    comparison: 'Officer by officer',
    howToRead:
      'Read a row across, never one column down. A low multiplier is not evidence on its own — it is also what small holdings and first offences produce. Recovery is the column that is hard to fake: it shows whether an order was collected, not merely passed.',
    officer: 'Officer',
    decided: 'Cases decided',
    cases: 'Cases',
    acres: 'Acres',
    season: 'Season',
    multiplier: 'Multiplier',
    multiplierNote: 'average ×{{avg}}',
    imposed: 'Fines imposed',
    recovered: 'Recovered',
    recoveredNote: '{{rate}} of what you imposed',
    recoveryRate: 'Recovered %',
    tooFew: 'fewer than {{n}} cases — too few to compare',
    omitted:
      '{{count}} officer(s) with the fewest decisions are not listed, to keep this record within its size limit.',
    noFlags: 'No officer in this jurisdiction meets any of the flag rules.',
    rulesToggle: 'What raises a flag',
    rulesIntro:
      'A flag is a question to put to the officer, not a finding against them. Each rule below is fixed and published, and every flag shows the figures that fired it.',
    rules: {
      minimumOnly:
        'Every fine imposed at \u00d71, across {{n}} or more of them. The law allows \u00d71 to \u00d710, so never leaving the floor is discretion that was not exercised.',
      belowPeers:
        'An average multiplier at or below {{pct}}% of the median for officers in the same jurisdiction.',
      notCollected:
        'Less than {{pct}}% recovered on demands older than {{days}} days. Recent decisions and days under a court stay are excluded, so nobody is marked down for work that has had no time to be paid.',
      strongRecovery:
        'Fines imposed at or above the median for the jurisdiction, AND {{pct}}% or more of the matured demands actually banked.',
    },
    rulesNotPositional:
      'No rule here is positional \u2014 there is no flag for being last in the table. A jurisdiction where every officer is working properly raises nothing, and imposing heavier fines is never in itself a way to clear a flag.',
    flag: {
      MINIMUM_ONLY: 'Never left \u00d71',
      BELOW_PEERS: 'Well below peers',
      NOT_COLLECTED: 'Largely uncollected',
      STRONG_RECOVERY: 'Strong recovery',
    },
    why: {
      MINIMUM_ONLY:
        'All {{finedCases}} fines imposed at the statutory minimum. The range available was \u00d71 to \u00d710.',
      BELOW_PEERS:
        'Average \u00d7{{average}} against a median of \u00d7{{peerMedian}} for officers in this jurisdiction.',
      NOT_COLLECTED:
        'Rs {{maturedImposed}} imposed on {{maturedCases}} demands older than {{days}} days; {{rate}}% recovered.',
      STRONG_RECOVERY:
        'Rs {{imposed}} imposed against a median of Rs {{peerMedian}}, and {{rate}}% of matured demands recovered.',
    },
    rank: {
      recovered: 'By recovered',
      imposed: 'By imposed',
      cases: 'By caseload',
      severity: 'By multiplier',
    },
  },

  handover: {
    title: 'Handovers',
    intro:
      'Work left behind when an officer was transferred out of this jurisdiction. Nothing here has been reassigned — who takes it over is a departmental decision.',
    none: 'No transfers have left work outstanding here.',
    left: '{{name}} has been transferred out',
    until: 'held the post until {{date}}',
    snapshotLag:
      'list compiled {{days}} days after the posting ended, so it describes that later date',
    nothingOutstanding: 'Nothing was outstanding when this officer left.',
    unreachableBanner:
      '{{count}} item(s) can no longer be actioned by anyone, including an administrator. See below.',
    state: {
      OPEN: 'Not yet taken up',
      ACKNOWLEDGED: 'Taken up',
    },
    kind: {
      wire: 'Theft wire',
      case: 'Tawan case',
      voucher: 'Challan 32-A',
    },
    note: {
      draftWireUnreachable:
        'Never dispatched. Only its author could dispatch it, and their access has moved — so nobody can now, and it will be purged when the retention window closes. Its contents must be re-entered by a serving Sub-Engineer if the theft is still to be pursued.',
      wireAwaitingSdo: 'Dispatched and still waiting on the SDO.',
      wireAwaitingConversion: 'Confirmed by the SDO; the Tawan case has not been framed.',
      voucherFailed: 'The treasury has no such credit. The paper receipt has to be traced.',
      voucherUnproven: 'Entered but not yet confirmed against the treasury scroll.',
    },
    noteLabel: 'Who is taking this work over?',
    noteHint:
      'Name the officers and what each of them is picking up. This is the only record of the decision — nothing is reassigned automatically.',
    acknowledge: 'Record the handover',
    acknowledgedBy: 'Taken up on {{date}}',
    readOnlyNote:
      'Your sub-division’s outstanding work, for information. Recording who takes it over is for the SDO or above.',
    error: {
      notSupervisor: 'Recording a handover is for the SDO or above.',
      alreadyAcknowledged: 'This handover has already been taken up.',
      noteTooShort: 'Name the officers picking the work up, and what each of them takes.',
    },
  },

  directory: {
    intro:
      'Who may sign in at all. Enrolling an address does not create an account — Google does that when the officer first signs in — so access appears within about fifteen minutes of their first attempt, not immediately.',
    enrolTitle: 'Enrol an officer',
    email: 'Google address',
    fullName: 'Name',
    designation: 'Designation',
    role: 'Role',
    enrol: 'Add to the directory',
    willMatchAs: 'Will be matched as:',
    awaitingTitle: 'Enrolled, not yet signed in',
    awaitingNote:
      'These addresses are in the directory but no Google account has matched them yet. That is normal before an officer’s first sign-in — but an address that stays here is usually a typo, and the officer will see “no access yet” however long they wait.',
    activeTitle: 'Active',
    noneActive: 'Nobody has signed in yet.',
    withdrawnTitle: 'Withdrawn',
    withdrawnNote:
      'Kept rather than deleted. A deleted row is indistinguishable from “never enrolled”, and would let the officer straight back in the next time they signed in.',
    withdraw: 'Withdraw access',
    reinstate: 'Reinstate',
    error: {
      emailRequired: 'Enter the officer’s Google address.',
      nameRequired: 'Enter the officer’s name.',
      roleRequired: 'Choose a role.',
      zoneRequired: 'Choose the zone this officer belongs to.',
      subDivisionRequiredForRole:
        'This role works in one sub-division. Without it the account would be created and able to do nothing.',
    },
  },

  admin: {
    tab: {
      directory: 'Directory',
      schedules: 'Penalty schedules',
      officers: 'Officers and transfers',
      import: 'Bulk import',
      audit: 'Audit log',
      retention: 'Retention',
    },
    schedule: {
      additionalRatesNote:
        'Also in the notification, recorded but NOT applied to any demand — whether an additional abiana rate forms part of a theft penalty, and how a per-annum figure combines with a per-season one, are departmental questions:',
      gardensRate: 'Sanctioned gardens, per acre per annum',
      liftRate: 'State lift irrigation, per acre per annum',
      enter: 'Enter a notification',
      enterTitle: 'Enter a penalty schedule',
      enterIntro:
        'From the Abiana notification itself. What you enter here cannot price a case until a SECOND administrator verifies it against the paper.',
      version: 'Version (becomes the record id)',
      notificationNumber: 'Notification number',
      notificationDate: 'Notification date',
      effectiveFrom: 'In force from',
      kharifRate: 'Kharif rate, PKR per acre',
      rabiRate: 'Rabi rate, PKR per acre',
      roundToNearest: 'Round the demand to the nearest',
      fileLocation: 'Where the paper is filed (optional)',
      secondAdminNote:
        'This will be saved UNVERIFIED. A second administrator must check it against the notification before any case can be priced. You cannot verify your own entry.',
      save: 'Save as unverified',
      none: 'No penalty schedule has been entered.',
      noneVerified:
        'No verified penalty schedule exists, so the system will refuse to price any case. That refusal is deliberate: it will not compute a legal money demand from numbers nobody has checked against a notification. Enter the current notification and have a second administrator verify it.',
      placeholderWarning:
        'This is the seeded placeholder. Its rates were invented for development and it can never be verified.',
      verified: 'Verified',
      unverified: 'Not yet verified',
      open: 'still in force',
      enteredBy: 'Entered by',
      verifiedBy: 'Verified by',
      notYet: 'not yet',
      confirmLabel: 'Notification number, from the paper in front of you',
      confirmHint:
        'Type it rather than copying it. The check is that somebody read the gazette, not that somebody clicked yes.',
      verify: 'Verify this schedule',
    },
    officers: {
      none: 'No officer has been recorded yet.',
      pendingSync:
        '{{count}} officer(s) have a posting recorded whose access has not moved yet. The claims sweep applies it within fifteen minutes — until then they still hold their previous access.',
      accessPending: 'Access not moved yet',
      noPosting: 'holds no posting — can see nothing',
      since: 'since {{date}}',
      sinceBeforeRecords: 'held since before this system recorded postings',
      present: 'present',
      history: 'Service record',
      transfer: 'Record a transfer',
      newRole: 'Role in the new posting',
      zone: 'Zone',
      circle: 'Circle',
      division: 'Division',
      subDivision: 'Sub-division',
      effectiveFrom: 'Takes charge on',
      recordTransfer: 'Record the transfer',
      accessNote:
        'This records the posting. Access follows when the claims sweep next runs, within fifteen minutes — the officer keeps their previous access until then, and this screen will say so.',
      handoverNote:
        'Any open wires or cases this officer leaves behind are listed overnight for the sub-division they are leaving, on that sub-division’s Handovers screen — not here, because an administrator does not read case content. Nothing is reassigned automatically; who takes the work over is a departmental decision recorded there.',
    },
    import: {
      adminOnly: 'Bulk import is an administrator function.',
      entity: 'What to import',
      entityName: {
        officers: 'Officers',
        subdivisions: 'Sub-divisions',
      },
      pastRuns: 'Past imports',
      noRuns: 'No import has been run yet.',
      runCounts: '{{create}} created, {{update}} updated',
      rollback: 'Reverse this import',
      rollbackConfirm: 'Yes, reverse it',
      rollbackWarning:
        'This will reverse {{count}} row(s): rows this import created are deactivated, and rows it changed are restored to what they were. Created rows are not deleted, because a case framed since may already reference them.',
      alreadyRolledBack: 'Already reversed.',
      downloadTemplate: 'Download the template',
      chooseFile: 'Choose a CSV file',
      dryRunNote:
        'Nothing is written until you have seen the diff. The file is parsed and compared against what exists; you choose whether to commit.',
      planning: 'Reading the file and comparing...',
      planHeading: 'What this import would do',
      outcome: {
        create: 'Create',
        update: 'Update',
        skip: 'Unchanged',
        error: 'Rejected',
      },
      showChanges: 'Show exactly which fields change',
      fileErrors: 'The file could not be read cleanly ({{count}} problem(s)):',
      line: 'Line {{line}}',
      someRowsRejected:
        '{{count}} row(s) were rejected. The rest can still be committed - correct these and import them separately.',
      downloadErrors: 'Download the error report',
      commit: 'Commit {{count}} row(s)',
      committed: 'Committed {{count}} row(s). Run id:',
    },
    audit: {
      note: 'The {{count}} most recent entries. The log is append-only and permanent - no entry here can be edited or removed by anyone, including an administrator.',
      when: 'When',
      who: 'Role',
      what: 'Action',
      subject: 'Subject',
    },
    retention: {
      intro:
        'Almost nothing in this system is ever deleted. Retention is the deliberate exception, and it points the other way: for these two categories, keeping the record for ever is itself the harm.',
      falseReports: 'Citizen reports closed as false, after inspection',
      draftWires: 'Wires drafted and never dispatched',
      permanent: 'Cases, the audit log and penalty schedules',
      days: '{{count}} days',
      never: 'never expire',
      signOffPending:
        'These periods await data-protection sign-off from the Department (PART K9). They are enforced by the nightly job as they stand.',
    },
  },

  csv: {
    error: {
      empty: 'The file is empty.',
      duplicateColumn: 'Two columns share a name.',
      wrongColumnCount:
        'This row has a different number of columns from the header - usually an unquoted comma.',
      strayQuote: 'A quotation mark appears in the middle of an unquoted field.',
      unterminatedQuote: 'A quotation mark is never closed, which swallows the rest of the file.',
      textAfterQuote: 'There is text after a closing quotation mark.',
    },
  },

  import: {
    error: {
      requiredFieldBlank: 'A required field is blank.',
      unknownRole: 'That role does not exist in this system.',
      subDivisionRequiredForRole:
        'This role works at a sub-division, and without one the account can do nothing at all.',
      phoneFormat: 'The phone number should look like 03001234567.',
      idBlank: 'The identifier column is blank.',
      duplicateIdInFile: 'Another row in this file claims the same identifier.',
      missingColumns: 'The file is missing one or more required columns.',
      fileMalformed: 'The file could not be read cleanly, so no row can be trusted.',
      nothingToDo: 'This file would change nothing.',
      fatal: 'This file cannot be imported.',
    },
  },

  schedule: {
    error: {
      versionInvalid: 'Use letters, digits, dots, dashes or underscores - at least three.',
      versionReserved: 'That version is reserved for the seeded placeholder.',
      notificationNumberRequired: 'Enter the notification number.',
      notificationDateRequired: 'Enter the notification date.',
      effectiveFromRequired: 'Enter the date these rates take effect.',
      effectiveToInvalid: 'That end date is not a date.',
      effectiveToBeforeFrom: 'The schedule would close before it opens.',
      effectiveBeforeNotification:
        'Rates cannot take effect before the notification that created them.',
      ratesRequired: 'Enter at least one rate.',
      rateNegative: 'A rate cannot be negative.',
      ratesAllZero: 'Every rate is zero. That is what the placeholder carries.',
      roundingInvalid: 'Rounding must be a whole number of rupees, at least one.',
      alreadyVerified: 'This schedule has already been verified.',
      sameAsAuthor:
        'You entered this schedule, so you cannot verify it. The whole point is that somebody else checked.',
      confirmNotificationNumber: 'Type the notification number from the paper.',
      notificationNumberMismatch:
        'That does not match the number on this schedule. Check you have the right notification in front of you.',
    },
  },

  assignment: {
    error: {
      roleRequired: 'Choose the role for the new posting.',
      effectiveFromRequired: 'Enter the date the officer takes charge.',
      zoneRequired: 'Enter the zone.',
      subDivisionRequiredForRole:
        'This role works at a sub-division. Without one the officer would be able to do nothing at all.',
      notAfterCurrentPosting:
        'A posting must begin after every posting this officer already holds. A service record cannot be written into the past.',
      noChange: 'That is the posting the officer already holds.',
    },
  },
};

export type TranslationShape = typeof en;
